import { useEffect, useState } from 'react';
import { AssetUrls } from 'config/assets';

/** Used while the sample loads, on a load error, or when the canvas is tainted. */
export const BANNER_FALLBACK_COLOR = '#3a1d16';

const SAMPLE_SIZE = 32;

/** One sample per URL per session (Asana 1219748562973072, Decision: Standard art box colour). */
const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string>>();

const sample = (url: string): Promise<string> =>
  new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = SAMPLE_SIZE;
        canvas.height = SAMPLE_SIZE;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(BANNER_FALLBACK_COLOR);
          return;
        }
        ctx.drawImage(img, 0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        const { data } = ctx.getImageData(0, 0, SAMPLE_SIZE, SAMPLE_SIZE);
        let r = 0;
        let g = 0;
        let b = 0;
        const pixels = data.length / 4;
        for (let i = 0; i < data.length; i += 4) {
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
        }
        resolve(`rgb(${Math.round(r / pixels)}, ${Math.round(g / pixels)}, ${Math.round(b / pixels)})`);
      } catch {
        // A tainted canvas throws on getImageData.
        resolve(BANNER_FALLBACK_COLOR);
      }
    };
    img.onerror = () => resolve(BANNER_FALLBACK_COLOR);
    img.src = url;
  });

/**
 * The average colour of an image as `rgb(r, g, b)`, for the Standard hover
 * card's art box. The fallback colour is returned while loading and whenever
 * the image cannot be sampled; nothing is logged.
 */
export const useAverageImageColor = (imageUrl: string | null | undefined): string => {
  const url = imageUrl ? AssetUrls.cards.ensureCDNUrl(imageUrl) : null;
  const [color, setColor] = useState<string>(() => (url && cache.get(url)) || BANNER_FALLBACK_COLOR);

  useEffect(() => {
    if (!url) {
      setColor(BANNER_FALLBACK_COLOR);
      return undefined;
    }
    const cached = cache.get(url);
    if (cached) {
      setColor(cached);
      return undefined;
    }
    let cancelled = false;
    let promise = inflight.get(url);
    if (!promise) {
      promise = sample(url).then((result) => {
        cache.set(url, result);
        inflight.delete(url);
        return result;
      });
      inflight.set(url, promise);
    }
    promise.then((result) => {
      if (!cancelled) setColor(result);
    });
    return () => {
      cancelled = true;
    };
  }, [url]);

  return color;
};

export default useAverageImageColor;
